/**
 * 큐베이스 점검 모드(쓰기 동결) 감지
 *
 * 큐베이스가 DB 이전 중 쓰기를 동결하면
 *  - 모든 응답에 `X-QBase-Maintenance: <phase>` + `Retry-After` 헤더가 실리고
 *  - 막힌 쓰기는 503 + {resultCode:"MAINTENANCE", maintenance:{...}} (일부 컨트롤러는 500 + "[MAINTENANCE] ...")
 *  - 전환(SWITCHING) 단계에서는 재기동으로 15~30초 연결이 끊긴다 (네트워크 오류 / 502 / 504)
 *
 * 이 모듈은 전역 fetch(관찰만, 응답은 그대로 돌려줌)와 BaseService(axios) 인터셉터에서 응답을 보고
 * 점검 상태를 유지한다. 점검 중에는 상태 API 를 retryAfterSec 마다 폴링해 해제를 감지한다.
 * 점검은 로그아웃/세션 만료 처리의 사유가 아니다 — 세션 처리 코드는 isMaintenanceActive() 로 확인한다.
 */

const BE_API = process.env.REACT_APP_BE_API;
const STATUS_URL = `${BE_API}/qbase/api/maintenance`;
const HEADER_NAME = 'X-QBase-Maintenance';
const DEFAULT_RETRY_SEC = 30;
const MAINTENANCE_PREFIX = '[MAINTENANCE]';

export const MAINTENANCE_ERROR_CODE = 'MAINTENANCE';

const INACTIVE = Object.freeze({ active: false });

let state = INACTIVE;
let lastPhase = null;
let pollTimer = null;
let lastPollAt = 0;
let installed = false;
let baseFetch = null;
const listeners = new Set();

const emit = () => listeners.forEach((fn) => fn());

export const subscribeMaintenance = (fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
};

export const getMaintenanceState = () => state;

export const isMaintenanceActive = () => state.active === true;

/** 마지막으로 알려진 단계가 DB 전환 중인지 — 이때의 연결 끊김은 재기동으로 본다 */
const isSwitching = () => lastPhase === 'SWITCHING';

const isQbaseUrl = (url) => {
    if (!BE_API) return false;
    const s = typeof url === 'string' ? url : (url && url.url) || String(url);
    return s.startsWith(BE_API) || s.includes(`${BE_API}/`);
};

const retrySecOf = (info) => {
    const n = Number(info && info.retryAfterSec);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_RETRY_SEC;
};

const setActive = (patch) => {
    const next = { ...(state.active ? state : {}), ...patch, active: true };
    if (next.phase) lastPhase = next.phase;
    state = next;
    emit();
    schedulePoll();
};

const clearActive = () => {
    if (!state.active) return;
    state = INACTIVE;
    lastPhase = null;
    if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
    }
    emit();
};

/** 503/500 본문이 점검 응답인지 */
export const isMaintenanceBody = (body) => {
    if (!body || typeof body !== 'object') return false;
    if (body.resultCode === MAINTENANCE_ERROR_CODE) return true;
    const msg = body.resultMessage || body.message;
    return typeof msg === 'string' && msg.startsWith(MAINTENANCE_PREFIX);
};

const applyBody = (body) => {
    const info = body.maintenance && typeof body.maintenance === 'object' ? body.maintenance : {};
    setActive({ ...info, unreachable: false });
};

/** 헤더 값(phase)만 알 때 — 세부 내용(메시지·해제 시각)은 폴링으로 채운다 */
const applyHeader = (phase, retryAfter) => {
    const wasActive = state.active;
    const patch = { phase, unreachable: false };
    const n = Number(retryAfter);
    if (Number.isFinite(n) && n > 0) patch.retryAfterSec = n;
    setActive(patch);
    if (!wasActive) pollNow();
};

/** 연결 끊김(네트워크 오류 / 502 / 504) — 전환 단계였으면 재기동 중으로 표시 */
const applyUnreachable = () => {
    if (!isSwitching()) return false;
    setActive({ phase: 'SWITCHING', unreachable: true });
    return true;
};

/**
 * 응답 하나를 관찰한다. body 는 503/500 일 때만 필요 — readJson 은 그때만 호출된다.
 */
const observe = async (status, getHeader, readJson) => {
    const phase = getHeader(HEADER_NAME);
    if (phase) {
        applyHeader(phase, getHeader('Retry-After'));
    } else if (state.active && status >= 200 && status < 400) {
        // 동결 중엔 모든 응답에 헤더가 실린다 — 헤더 없는 정상 응답이면 해제됐을 수 있으니 즉시 확인
        pollNow();
    }

    if (status === 503 || status === 500) {
        try {
            const body = await readJson();
            if (isMaintenanceBody(body)) {
                applyBody(body);
                return;
            }
        } catch (e) {
            // JSON 아님 — 점검 응답이 아니다
        }
    }
    if (status === 502 || status === 504 || status === 503) {
        applyUnreachable();
    }
};

// ── 폴링 ──────────────────────────────────────────────

const POLL_MIN_GAP_MS = 5000;

const pollNow = () => {
    // 헤더 없는 응답이 연달아 와도 상태 API 는 짧은 간격으로 한 번만 부른다
    if (Date.now() - lastPollAt < POLL_MIN_GAP_MS) return;
    if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
    }
    fetchStatus();
};

function schedulePoll() {
    if (pollTimer || !state.active) return;
    pollTimer = setTimeout(() => {
        pollTimer = null;
        fetchStatus();
    }, retrySecOf(state) * 1000);
}

async function fetchStatus() {
    lastPollAt = Date.now();
    const doFetch = baseFetch || window.fetch.bind(window);
    try {
        const res = await doFetch(STATUS_URL, {
            method: 'GET',
            credentials: 'include',
            headers: { Accept: 'application/json' },
            cache: 'no-store',
        });
        if (!res.ok) {
            // 재기동 중(502/504/503) — 점검 상태 유지
            if (!applyUnreachable() && state.active) schedulePoll();
            return;
        }
        const body = await res.json();
        const info = body && body.maintenance;
        if (info && info.active) {
            setActive({ ...info, unreachable: false });
        } else {
            clearActive();
        }
    } catch (e) {
        // 연결 끊김 — 점검 중이면 계속 기다린다
        if (!applyUnreachable() && state.active) schedulePoll();
    }
}

// ── 설치 ──────────────────────────────────────────────

/**
 * 전역 fetch 에 관찰자를 건다. 응답/오류는 호출자에게 그대로 전달한다 (재전송·변형 없음).
 * RUM 이 fetch 를 감쌀 수 있으므로 initRUM 보다 먼저 호출한다.
 */
export const installMaintenanceObserver = () => {
    if (installed || typeof window === 'undefined' || !window.fetch || !BE_API) return;
    installed = true;
    baseFetch = window.fetch.bind(window);

    window.fetch = async (input, init) => {
        const qbase = isQbaseUrl(input);
        let res;
        try {
            res = await baseFetch(input, init);
        } catch (err) {
            if (qbase && err && err.name !== 'AbortError') applyUnreachable();
            throw err;
        }
        if (qbase) {
            observe(
                res.status,
                (name) => res.headers.get(name),
                () => res.clone().json()
            );
        }
        return res;
    };

    // 새로고침 직후에도 점검 중이면 바로 띠를 띄운다
    fetchStatus();
};

/** BaseService(axios) 응답 인터셉터용 */
export const observeAxiosResponse = (response) => {
    if (!response || !isQbaseUrl(`${response.config?.baseURL || ''}${response.config?.url || ''}`)) return;
    const headers = response.headers || {};
    observe(
        response.status,
        (name) => headers[name.toLowerCase()],
        async () => response.data
    );
};

/** BaseService(axios) 오류 인터셉터용 */
export const observeAxiosError = (error) => {
    const cfg = error && error.config;
    if (!cfg || !isQbaseUrl(`${cfg.baseURL || ''}${cfg.url || ''}`)) return;
    if (error.response) {
        observeAxiosResponse(error.response);
    } else if (error.code !== 'ERR_CANCELED') {
        applyUnreachable();
    }
};

// ── 저장 경로용 ───────────────────────────────────────

export const MAINTENANCE_SAVE_MESSAGE =
    '큐베이스 점검 중이라 지금은 저장할 수 없습니다. 작업 내용은 화면에 그대로 남아 있으니 점검이 끝난 뒤 다시 저장해 주세요.';

export const createMaintenanceError = (message = MAINTENANCE_SAVE_MESSAGE) => {
    const err = new Error(message);
    err.code = MAINTENANCE_ERROR_CODE;
    return err;
};

export const isMaintenanceError = (error) => !!error && error.code === MAINTENANCE_ERROR_CODE;

/**
 * fetch 응답이 점검 거부(503/500 + MAINTENANCE)면 MaintenanceError 를 던진다.
 * 본문은 clone 으로 읽으므로 호출자가 이어서 response.json() 을 읽을 수 있다.
 */
export const throwIfMaintenanceResponse = async (response) => {
    if (response.status !== 503 && response.status !== 500) return;
    let body = null;
    try {
        body = await response.clone().json();
    } catch (e) {
        body = null;
    }
    if (isMaintenanceBody(body)) {
        applyBody(body);
        throw createMaintenanceError();
    }
    if (response.status === 503 && isSwitching()) {
        throw createMaintenanceError();
    }
};

/** 네트워크 오류가 DB 전환 재기동 중에 난 것이면 점검 오류로 바꿔 준다 */
export const toMaintenanceErrorIfSwitching = (error) => {
    if (isMaintenanceError(error)) return error;
    if (error && error.name === 'TypeError' && isSwitching()) return createMaintenanceError();
    return error;
};
